import Close from '@components/icons/Close/Close'
import Toggle from '@components/ui/Toggle/Toggle'

import styles from '@components/modules/ProductGrid/ProductGrid.module.scss'
import Button from '@components/ui/Button'
import {
  fixedClearAllBodyScrollLocks,
  fixedDisableBodyScroll,
} from '@lib/fixedScrollHelpers'
import { ProductGridTileFragment } from '@lib/graphql/graphql'
import useQueryState from '@lib/hooks/useQueryState/useQueryState'
import useRecommendations, {
  EventType,
  ServingConfig,
} from '@lib/hooks/useRecommendations'
import clsx from 'clsx'
import { queryTypes } from 'next-usequerystate'
import { useRouter } from 'next/router'
import { FC, useEffect, useRef, useState } from 'react'
import { MdChevronRight } from 'react-icons/md'
import FilterCheckbox from './Filter/FilterCheckbox'
import FilterDropdown, { FilterDropdownOption } from './Filter/FilterDropdown'
import {
  UrlFilter,
  getLabel,
  parseQueryAttributeFilters,
  serializeQueryAttributeFilters,
} from './Filter/filtering'
import ProductCollection from './ProductCollection/ProductCollection'
import SortingDropdown from './Sorting/SortingDropdown'
import {
  UrlSorting,
  parseQuerySort,
  serializeQuerySort,
} from './Sorting/sorting'

type ProductGridProps = {
  products?: ProductGridTileFragment[]
  tags?: string[]
  sortDisabled?: boolean
  categoryFilter?: boolean
  typeFilter?: boolean
  designerFilter?: boolean
  collectionFilter?: boolean
  buyOnline?: boolean
  recommendation?: boolean
  recommendationServingConfig?: ServingConfig
  recommendationEventType?: EventType
  recommendationSize?: number
  recommendationsMixProducts?: boolean
  productsFromPageTags?: boolean
  lcp: boolean
}
export type filterState = {
  name: string
  options: FilterDropdownOption[]
}
const ProductGrid: FC<ProductGridProps> = ({
  products: initialProducts,
  tags,
  sortDisabled,
  categoryFilter,
  typeFilter,
  designerFilter,
  collectionFilter,
  buyOnline,
  recommendation,
  recommendationServingConfig,
  recommendationEventType,
  recommendationSize,
  recommendationsMixProducts,
  productsFromPageTags,
  lcp,
}) => {
  const router = useRouter()

  const [filterOpen, setFilterOpen] = useState(false)
  const [soldOnline, setSoldOnline] = useQueryState(
    'soldOnline',
    queryTypes.boolean.withDefault(router.query.soldOnline === 'true'),
  )

  const [products, setProducts] = useState(initialProducts)
  const [queryFilters, setQueryFilters] = useQueryState('filters', {
    parse: parseQueryAttributeFilters,
    serialize: serializeQueryAttributeFilters,
    defaultValue: [],
  })
  const [sortByQuery, setSortByQuery] = useQueryState('sortBy')
  const [sortBy, setSortBy] = useState<UrlSorting>()

  useEffect(() => {
    setSortBy(parseQuerySort(sortByQuery))
  }, [sortByQuery])

  useEffect(() => {
    if (queryFilters.length && !products) return
    if (queryFilters.length > 0) {
      setProducts(
        initialProducts.filter((product) => {
          if (!queryFilters) return true
          return queryFilters.every((f) => {
            if (
              f.slug === 'categories' ||
              f.slug === 'designers' ||
              f.slug === 'types'
            ) {
              return f.values.every((val) =>
                product.tags.some(
                  (tag) => tag.toLowerCase() === val.toLowerCase(),
                ),
              )
            }
            if (f.slug === 'collections') {
              return f.values.some((val) =>
                product.tags.some(
                  (tag) => tag.toLowerCase() === val.toLowerCase(),
                ),
              )
            }

            return false
          })
        }),
      )
    } else {
      setProducts(initialProducts)
    }
  }, [queryFilters])

  const categories = [
    ...new Set(
      initialProducts
        ?.map((product) =>
          product.tags
            ?.filter((tag) => tag.startsWith('cat_'))
            .map((tag) => tag),
        )
        .flat(),
    ),
  ].map((tagName) => ({
    value: tagName,
    label: getLabel(tagName),
    count: products?.reduce(
      (count, product) =>
        product.tags?.find((tag) => tag === tagName) ? count + 1 : count,
      0,
    ),
  }))

  const types = [
    ...new Set(
      initialProducts
        ?.map((product) =>
          product.tags
            ?.filter((tag) => tag.startsWith('type_'))
            .map((tag) => tag),
        )
        .flat(),
    ),
  ].map((tagName) => ({
    value: tagName,
    label: getLabel(tagName),
    count: products?.reduce(
      (count, product) =>
        product.tags?.find((tag) => tag === tagName) ? count + 1 : count,
      0,
    ),
  }))

  const designers = [
    ...new Set(
      initialProducts
        ?.map((product) =>
          product.tags
            ?.filter((tag) => tag.startsWith('designer_'))
            .map((tag) =>
              JSON.stringify({
                name: tag,
                label:
                  product.name.includes(tag.split('_')[1]) &&
                  product.name.includes('-')
                    ? getLabel(tag)
                    : getLabel(tag).replace(/-/gm, ' '),
              }),
            ),
        )
        .flat(),
    ),
  ].map((unparsedTag) => {
    if (!unparsedTag) return
    const parsedTag = JSON.parse(unparsedTag)
    return {
      value: parsedTag.name,
      label: parsedTag.label,
      count: products?.reduce(
        (count, product) =>
          product.tags.find((tag) => tag === parsedTag.name)
            ? count + 1
            : count,
        0,
      ),
    }
  })

  const collections = [
    ...new Set(
      initialProducts
        ?.map((product) =>
          product.tags
            ?.filter((tag) => tag.startsWith('col_'))
            .map((tag) =>
              JSON.stringify({
                name: tag,
                label:
                  product.name.includes(tag.split('_')[1]) &&
                  product.name.includes('-')
                    ? getLabel(tag)
                    : getLabel(tag).replace(/-/gm, ' '),
              }),
            ),
        )
        .flat(),
    ),
  ].map((unparsedTag) => {
    if (!unparsedTag) return
    const parsedTag = JSON.parse(unparsedTag)
    return {
      value: parsedTag.name,
      label: parsedTag.label,
      count: products?.reduce(
        (count, product) =>
          product.tags.find((tag) => tag === parsedTag.name)
            ? count + 1
            : count,
        0,
      ),
    }
  })

  const onChangeQueryFilter = (
    slug: string,
    options: FilterDropdownOption[],
  ) => {
    //check if filter is already applied for that slug
    const existingFilter = queryFilters.find((x) => x.slug === slug)
    if (!existingFilter) {
      return setQueryFilters([
        ...queryFilters,
        { slug: slug, values: options.map((x) => x.value) },
      ])
    }

    existingFilter.values = options.map((x) => x.value)
    return setQueryFilters(queryFilters)
  }

  const removeQueryFilter = (slug: string, value: string) => {
    const newFilters = queryFilters.reduce(
      (result: UrlFilter[], filter: UrlFilter) => {
        if (filter.slug !== slug) {
          return [...result, filter]
        }
        const newFilterValues = filter.values.filter((val) => val !== value)
        if (newFilterValues?.length) {
          return [...result, { ...filter, values: newFilterValues }]
        }
        return result
      },
      [],
    )

    return setQueryFilters(newFilters.length ? newFilters : null)
  }

  const [openLists, setOpenLists] = useState<string>()
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (filterOpen) {
      fixedDisableBodyScroll(contentRef.current)
    }
  }, [filterOpen])

  const [recommendations, { loading }] = useRecommendations({
    mixProducts: recommendationsMixProducts && productsFromPageTags,
    fetchRecommendations: recommendation,
    servingConfig: recommendationServingConfig,
    eventType: recommendationEventType,
    pageSize: recommendationSize,
    products: products,
  })

  return (
    <div className={styles.root}>
      <div
        className={clsx(
          'filterOverlay',
          { [styles.open]: filterOpen },
          styles.filterOverlay,
        )}
        onTransitionEnd={(e) => !filterOpen && fixedClearAllBodyScrollLocks()}
        onClick={(e) =>
          e.target == document.querySelector('.filterOverlay') &&
          setFilterOpen(false)
        }
      >
        <div className={styles.container} ref={contentRef}>
          <div className={styles.wrapper}>
            <div className="headerTwo select-none mb-3 flex items-center justify-between">
              Filters
              <Close
                className="cursor-pointer w-8 h-8 p-1"
                onClick={() => setFilterOpen(false)}
              />
            </div>
            <div className={styles.content}>
              {buyOnline && (
                <>
                  <Toggle
                    enabled={soldOnline}
                    onChange={(value: boolean) => {
                      setSoldOnline(value)
                    }}
                  />

                  <br />
                </>
              )}

              <div className="optionsContainer">
                {categoryFilter && (
                  <FilterCheckbox
                    onClick={() =>
                      openLists == 'categories'
                        ? setOpenLists('')
                        : setOpenLists('categories')
                    }
                    isListOpen={openLists === 'categories'}
                    name="Category"
                    options={categories}
                    selectedOptions={categories?.filter((tag) =>
                      queryFilters.find(
                        (qf) =>
                          qf.slug == 'categories' &&
                          qf.values.includes(tag.value),
                      ),
                    )}
                    onChange={(options) =>
                      onChangeQueryFilter('categories', options)
                    }
                  />
                )}

                {typeFilter && (
                  <FilterCheckbox
                    onClick={() =>
                      openLists == 'types'
                        ? setOpenLists('')
                        : setOpenLists('types')
                    }
                    isListOpen={openLists === 'types'}
                    name="Type"
                    options={types}
                    selectedOptions={types?.filter((tag) =>
                      queryFilters.find(
                        (qf) =>
                          qf.slug == 'types' && qf.values.includes(tag.value),
                      ),
                    )}
                    onChange={(options) =>
                      onChangeQueryFilter('types', options)
                    }
                  />
                )}

                {designerFilter && (
                  <FilterCheckbox
                    onClick={() =>
                      openLists == 'designers'
                        ? setOpenLists('')
                        : setOpenLists('designers')
                    }
                    isListOpen={openLists === 'designers'}
                    name="Designer"
                    options={designers}
                    selectedOptions={designers?.filter((tag) =>
                      queryFilters.find(
                        (qf) =>
                          qf.slug == 'designers' &&
                          qf.values.includes(tag.value),
                      ),
                    )}
                    onChange={(options) =>
                      onChangeQueryFilter('designers', options)
                    }
                  />
                )}

                {collectionFilter && (
                  <FilterCheckbox
                    onClick={() =>
                      openLists == 'collections'
                        ? setOpenLists('')
                        : setOpenLists('collections')
                    }
                    isListOpen={openLists === 'collections'}
                    name="Collection"
                    options={collections}
                    selectedOptions={collections?.filter((tag) =>
                      queryFilters.find(
                        (qf) =>
                          qf.slug == 'collections' &&
                          qf.values.includes(tag.value),
                      ),
                    )}
                    onChange={(options) =>
                      onChangeQueryFilter('collections', options)
                    }
                  />
                )}
              </div>
            </div>
            <div className={styles.buttonsWrapper}>
              <div className={styles.buttonsContainer}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    delete router.query.filters
                    delete router.query.soldOnline
                    router.push(router, undefined, { shallow: true })
                  }}
                >
                  Clear
                </Button>
                <Button variant="primary" onClick={() => setFilterOpen(false)}>
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={clsx('hideOnMobile', styles.filterContainer)}>
        <div className="gap-6 flex items-center justify-end">
          {buyOnline && (
            <Toggle
              enabled={soldOnline}
              onChange={(value: boolean) => {
                setSoldOnline(value)
              }}
            />
          )}
          {categoryFilter && (
            <FilterDropdown
              options={categories?.map((tag) => ({
                label: tag.label,
                value: tag.value,
                count: tag.count,
                disabled: tag.count === 0,
              }))}
              selectedOptions={categories?.filter((tag) =>
                queryFilters.find(
                  (qf) =>
                    qf.slug == 'categories' && qf.values.includes(tag.value),
                ),
              )}
              placeholder={'Category'}
              onChange={(options) => onChangeQueryFilter('categories', options)}
            />
          )}

          {typeFilter && (
            <FilterDropdown
              options={types?.map((tag) => ({
                label: tag.label?.replace(/-/gm, ' '),
                value: tag.value,
                count: tag.count,
                disabled: tag.count === 0,
              }))}
              selectedOptions={types?.filter((tag) =>
                queryFilters.find(
                  (qf) => qf.slug == 'types' && qf.values.includes(tag.value),
                ),
              )}
              placeholder={'Type'}
              onChange={(options) => onChangeQueryFilter('types', options)}
            />
          )}

          {designerFilter && (
            <FilterDropdown
              options={designers?.map((tag) => ({
                label: tag.label,
                value: tag.value,
                count: tag.count,
                disabled: tag.count === 0,
              }))}
              selectedOptions={designers?.filter((tag) =>
                queryFilters.find(
                  (qf) =>
                    qf.slug == 'designers' && qf.values.includes(tag.value),
                ),
              )}
              placeholder={'Designer'}
              onChange={(options) => onChangeQueryFilter('designers', options)}
            />
          )}

          {collectionFilter && (
            <FilterDropdown
              options={collections?.map((tag) => ({
                label: tag.label,
                value: tag.value,
                count: -1,
                disabled: false,
              }))}
              selectedOptions={collections?.filter((tag) =>
                queryFilters.find(
                  (qf) =>
                    qf.slug == 'collections' && qf.values.includes(tag.value),
                ),
              )}
              placeholder={'Collection'}
              onChange={(options) =>
                onChangeQueryFilter('collections', options)
              }
            />
          )}

          {!sortDisabled && (
            <SortingDropdown
              optionToggle={(field, direction) => {
                if (sortBy?.field == field && sortBy?.direction == direction) {
                  setSortByQuery(null)
                  return
                }

                setSortByQuery(
                  serializeQuerySort(
                    field && direction ? { field, direction } : null,
                  ),
                )
              }}
              chosen={sortBy}
            />
          )}
        </div>
      </div>

      <div className={clsx('showOnMobile')}>
        <div className={styles.mobileFilter}>
          {(categoryFilter ||
            typeFilter ||
            designerFilter ||
            collectionFilter ||
            buyOnline) && (
            <div
              className={clsx(
                {
                  [styles.filterBy]:
                    (router.query.filters?.length > 0 &&
                      router.query.filters?.includes('.')) ||
                    soldOnline,
                },
                'paragraphOne',
              )}
              onClick={() => setFilterOpen(true)}
            >
              Filter By
              <MdChevronRight
                className={clsx('transition-transform ml-1', 'rotate-90')}
              />
            </div>
          )}
          {!sortDisabled && (
            <div>
              <SortingDropdown
                optionToggle={(field, direction) => {
                  if (
                    sortBy?.field == field &&
                    sortBy?.direction == direction
                  ) {
                    setSortByQuery(null)
                    return
                  }

                  setSortByQuery(
                    serializeQuerySort(
                      field && direction ? { field, direction } : null,
                    ),
                  )
                }}
                chosen={sortBy}
                isMobile
              />
            </div>
          )}
        </div>
      </div>

      <div className={clsx('hideOnMobile', styles.filterContainer)}>
        <div className="flex flex-wrap justify-end">
          {queryFilters &&
            queryFilters.map((x, index) =>
              x.values.map((val) => (
                <div key={index} className="flex ml-2 mt-2">
                  <div
                    className="select-none p-2 text-xs rounded-l-sm"
                    style={{
                      letterSpacing: '0.1rem',
                      backgroundColor: '#f8f7f7',
                    }}
                  >
                    {getLabel(val).replace(/-/gm, ' ')}
                  </div>
                  <button
                    title="Remove"
                    className="select-none p-2 text-xs rounded-r-sm bg-[#f8f7f7] hover:bg-[#eee]"
                    style={{
                      letterSpacing: '0.1rem',
                    }}
                    onClick={() => removeQueryFilter(x.slug, val)}
                  >
                    <Close className="cursor-pointer w-4 h-4" />
                  </button>
                </div>
              )),
            )}
        </div>
      </div>
      <ProductCollection
        filter={queryFilters}
        products={products}
        soldOnline={soldOnline}
        sortBy={sortBy}
        recommendations={recommendations}
        lcp={lcp}
      />
    </div>
  )
}

export default ProductGrid
